import type { PAGE_POSITIONS } from 'types';

import StarFull from '~/assets/icons/StarFull';
import AppleAppStoreLogo from '~/assets/img/apple-app-store.svg';
import Image from '~/components/Image';
import Tracked from '~/components/Tracked';
import type { NavigationLinkPlacement } from '~/events/NavigationLinkInteractionEvent';
import useNavigationLinkInteraction from '~/hooks/useNavigationLinkInteraction';
import useRegion from '~/hooks/useRegion';

const APP_STORE_BADGE_LABEL = 'Download on the App Store';

type Props = {
  downloadLink: string;
  pagePosition: PAGE_POSITIONS;
  qrCode: string;
};

export default function DownloadAppBlock({
  pagePosition,
  qrCode,
  downloadLink
}: Props) {
  const region = useRegion();

  if (region.code !== 'GB') return null;

  return (
    <section className="bg-dark-mode-black pt-8">
      <div className="content-width flex flex-col-reverse items-center justify-center gap-6 sm:flex-row sm:gap-8 md:min-h-[450px] md:gap-[12%]">
        <AppDownloadDisplayImage />
        <DisplayCopy
          pagePosition={pagePosition}
          qrCode={qrCode}
          downloadLink={downloadLink}
        />
      </div>
    </section>
  );
}

export function AppDownloadDisplayImage() {
  return (
    <div className="w-2/3 sm:w-2/5 sm:self-end md:w-1/3">
      <Image
        alt="Get release reminders with The Sole Supplier iOS app"
        className="mx-auto w-full"
        src="https://cms-cdn.thesolesupplier.co.uk/bolt/assets/images/iPhone_Jordan4_Bred_2026_Notification.png"
        loading="lazy"
      />
    </div>
  );
}

function DisplayCopy({ pagePosition, qrCode, downloadLink }: Props) {
  return (
    <div className="flex w-full flex-col gap-6 max-sm:px-2 sm:w-1/2 md:w-5/12">
      <Title />

      <DownloadLinks
        pagePosition={pagePosition}
        qrCode={qrCode}
        downloadLink={downloadLink}
      />
    </div>
  );
}

export function DownloadLinks({ pagePosition, qrCode, downloadLink }: Props) {
  return (
    <div className="flex w-full items-stretch justify-center gap-6 sm:justify-start">
      <QrCode qrCode={qrCode} />
      <RatingsBlock pagePosition={pagePosition} downloadLink={downloadLink} />
    </div>
  );
}

type StarsProps = {
  height?: string;
  width?: string;
};

export function Stars({ height = '24', width = '24' }: StarsProps) {
  return (
    <div className="flex gap-0.5">
      {new Array(5).fill(0).map((_, index) => (
        <div key={`star-rating-${index}`}>
          <span className="hidden lg:block">
            <StarFull fill="#FA0" height={height} width={width} />
          </span>
          <span className="lg:hidden">
            <StarFull fill="#FA0" height="16" width="16" />
          </span>
        </div>
      ))}
    </div>
  );
}

function RatingsBlock({
  pagePosition,
  downloadLink
}: {
  downloadLink: string;
  pagePosition: PAGE_POSITIONS;
}) {
  return (
    <div className="flex w-full items-center justify-center gap-4 text-white sm:w-fit sm:flex-col sm:items-start">
      <DownloadFromAppStoreLink
        className="max-sm:max-w-[110px]"
        href={downloadLink}
        pagePosition={pagePosition}
        placement="app_block"
      />

      <div className="flex items-center justify-center gap-2">
        <Stars />
        <span>4.8</span>
      </div>
    </div>
  );
}

export function Title({ alignDesktop }: { alignDesktop?: 'center' | 'left' }) {
  return (
    <div
      className={`flex w-full flex-col gap-2 text-center ${alignDesktop === 'center' ? 'sm:text-center' : 'sm:text-left'}`}
    >
      <p className="text-title-xl text-golden-syrup">Tired of missing drops?</p>
      <p className="text-title-xl text-white">Get the app.</p>
    </div>
  );
}

function QrCode({ qrCode }: { qrCode: string }) {
  return (
    <div className="hidden aspect-square w-[88px] lg:block">
      <Image loading="lazy" alt="QR Code to download the app" src={qrCode} />
    </div>
  );
}

export function DownloadFromAppStoreLink({
  className,
  href,
  pagePosition,
  placement,
  position = 1
}: {
  className?: string;
  href: string;
  pagePosition: PAGE_POSITIONS;
  placement: NavigationLinkPlacement;
  position?: number;
}) {
  const fireAppLink = useNavigationLinkInteraction(placement);

  return (
    <Tracked.ExternalLink
      className={`w-full ${className ?? ''}`}
      name="DOWNLOAD_THE_APP"
      onClick={() =>
        fireAppLink(href, position, { label: APP_STORE_BADGE_LABEL })
      }
      pagePosition={pagePosition}
      href={href}
      target="_blank"
      rel="noreferrer"
      variant="none"
    >
      <img
        alt={APP_STORE_BADGE_LABEL}
        className="w-full"
        src={AppleAppStoreLogo}
        loading="lazy"
      />
    </Tracked.ExternalLink>
  );
}
